import {Component, Input, OnChanges, SimpleChanges} from '@angular/core';
import {CmEdFieldView} from "../../interface/cm-ed-field-view.interface";
import {CmEdSize} from "../../interface/cm-ed-size.type";

/**
 * The band of Vertrag basics across the top of the card-led view — Versicherungsnummer, Beginn,
 * Beitrag, Status, side by side as figures rather than as a list of pairs.
 *
 * The band exists so the card grid can start immediately below it: the basics are the four or five
 * figures a clerk checks before reading anything else, and a key/value list of them costs the whole
 * upper third of the screen that the Deckungen are supposed to fill.
 *
 * <h2>The narrow degradation</h2>
 * At `xs` the figures would each end up on their own line — the exact list the band was chosen to
 * avoid, only taller. There the band renders as TWO LINES OF RUNNING TEXT instead
 * ("5426/8 810 442 · 01.04.2024 · 212,60 € · aktiv"), which says the same thing in two lines. The
 * labels are dropped from the text and kept as the `title` of each figure, because at that width a
 * label costs more room than the value it names.
 *
 * <h2>The qualifier travels with its figure</h2>
 * A delivered `qualifier` ("monatlich, Lastschrift") is rendered under the figure it qualifies, in
 * BOTH forms. It is the one part of a pair that may not be dropped when the labels are: a label is
 * still reachable through the figure's `title`, whereas a premium that has lost its Zahlungsweise is
 * simply a different number — and this band is the only Vertrag rendering the card-led view has.
 *
 * <h2>What it deliberately does NOT do</h2>
 * <ul>
 *   <li>It does not FORMAT and it does not COMPOSE. The running text is the server's own display
 *       values joined with "·" — no invented connective ("seit", "/ Monat"), no unit mapping and no
 *       re-derived figure. Everything printed here was delivered and stored exactly like this, the
 *       qualifier included: it is printed because the server sent that string, never assembled from
 *       a Zahlungsweise code.</li>
 *   <li>It does not SELECT which basics to show. It renders the fields it is given, in order.</li>
 *   <li>It does not STRETCH its figures across the width it is given. Each cell stays at its own
 *       content width up to a bound, so on a 2111 px host four basics sit packed at the left rather
 *       than 500 px apart — "utilise the width" is answered by the card grid UNDER this band, which
 *       turns the freed room into more columns, not by spacing four figures out into a sparse row.</li>
 *   <li>It surfaces no technical layer: the band shows the contract's own basics, which are typed
 *       columns rather than partner-specific bag fields, so a coordinate has nothing to say here.</li>
 *   <li>It carries no LEVEL ladder either. Every figure in the band is a Basisdatum by definition —
 *       that is what makes it a basic — so there is no second step to distinguish, and applying the
 *       ladder's quieter steps to a band of four figures would subdue the most important line on the
 *       card-led view.</li>
 *   <li>It does not COLOUR one figure above the others — not even the premium, which is the figure a
 *       clerk looks for first. Nothing in the payload says which cell that is: the band is handed a
 *       plain `CmEdFieldView[]` (`CmEdSheetView#contractFields`), and the DTO behind it carries
 *       label, value, unit, displayLevel, displayOrder, generic, unresolvedCode and the coordinate —
 *       no role, no kind, no semantic marker. `qualifier` is documented as a duplicate-LABEL
 *       disambiguator and not as a premium flag, so keying off it would be a coincidence, and
 *       matching the German word "Beitrag" would hardcode one partner's vocabulary into the one
 *       screen built to be partner-agnostic. The band ranks its figures by WEIGHT and SIZE instead,
 *       which needs no semantics. Colouring it becomes possible the day the server states which
 *       basic is the premium.</li>
 * </ul>
 */
@Component({
    selector: 'mvs-cm-ed-basis-band',
    templateUrl: './cm-ed-basis-band.component.html',
    styleUrls: ['./cm-ed-basis-band.component.scss'],
    standalone: false,
})
export class CmEdBasisBandComponent implements OnChanges {

    /** The Vertrag basics, in the order they are to be read. Never re-sorted, never filtered. */
    @Input() fields: CmEdFieldView[] = [];

    /** Width bucket, measured once by the host. `xs` switches the band to running text. */
    @Input() size: CmEdSize = 'lg';

    /**
     * True while a search term is being matched — the SAME flag every other renderer of a delivered
     * field receives, and the reason it had to arrive here too.
     *
     * The band is the card-led view's ONLY rendering of `CmEdSheetView#contractFields`, and the host
     * counts a hit on a contract basic into the header's "{n} Treffer" in every view. Without this
     * input the band was the one place where that count could be stated with nothing marked on
     * screen: a reader searching "Versicherungsnummer" was told the sheet held a match and shown a
     * band that looked exactly as it does at rest. That is the silent under-reporting the sheet's
     * honesty rule forbids, arriving through a component nobody thought of as a search surface.
     *
     * It marks and it never filters: a figure that did not match renders exactly as it does with no
     * term — same colour, same weight, same opacity — and no cell is ever dropped or reordered.
     */
    @Input() searching: boolean = false;

    /**
     * The running-text form: the printable fields split into at most two lines.
     *
     * Precomputed rather than built by the template, because the split is arithmetic and a template
     * that did it would redo it on every change detection cycle.
     */
    protected runningLines: CmEdFieldView[][] = [];

    /**
     * True while the band renders as running text instead of as figures.
     *
     * The floor of the ladder and nothing above it. Every step is written out rather than left to a
     * `default`, so a step added above `xxl` cannot silently inherit the degraded form.
     */
    get running(): boolean {

        switch (this.size) {
            case 'xxl':
            case 'xl':
            case 'lg':
            case 'md':
            case 'sm':
                return false;
            case 'xs':
                // Below 560 px four figures side by side become four lines — the list the band exists
                // to avoid, only taller. Two lines of running text say the same thing in less room.
                return true;
        }
    }

    /**
     * Type scale of a figure.
     *
     * `text-lg` from `md` up, i.e. from 760 px — the SAME pixel bound the band has always used. It
     * used to be spelled `xl || lg`, which meant ">= 760" under the old ladder and would mean
     * ">= 1100" under this one: left alone, the rename alone would have quietly demoted the figures on
     * every host between 760 and 1099 px. The bound is a property of the band (four figures need
     * ~760 px before they can carry a larger step without wrapping), not of a bucket's name.
     */
    get figureClass(): string {

        switch (this.size) {
            case 'xxl':
            case 'xl':
            case 'lg':
            case 'md':
                return 'text-lg';
            case 'sm':
                // 560-759 px: four figures at `text-lg` would wrap into three rows and stop reading as
                // a band at all.
                return 'text-base';
            case 'xs':
                // Never reached — `running` is true here and the figures are not rendered. Answered
                // anyway, so the getter cannot depend on which template happens to be live.
                return 'text-base';
        }
    }

    /**
     * The `title` of ONE figure — what the line says, so a figure the cell had to cut is still
     * readable in full.
     *
     * A cell is width-bounded and its figure ellipsises rather than wraps (see the template and
     * `.cm-ed-band-cell`), and the unit sits at the END, so the unit is the first thing a cut removes.
     * The title therefore restates value AND unit, separated by the same single space the rendered
     * `ml-1` already puts between them — it mirrors the line, it does not compose a new reading of it.
     *
     * `null` for a field with no display value, which REMOVES the attribute rather than offering an
     * empty tooltip: an empty figure has nothing to recover, and "null €" is worse than no title.
     */
    protected figureTitle(field: CmEdFieldView): string | null {

        if (!field?.displayValue) {
            return null;
        }

        return field.unit
            ? field.displayValue + ' ' + field.unit
            : field.displayValue;
    }

    /**
     * Whether the highlight the host computed lands inside the DISPLAY VALUE of this figure.
     *
     * Reads the host's runs and nothing else — it folds no text, sees no term and compares no
     * strings, so the band cannot mark a different set of characters than the header counted. The
     * sheet has exactly one matcher and this is not it.
     *
     * The running-text form needs the answer because it drops the LABEL: a hit that lives in the
     * label is invisible there, and a wash with no visible cause is what the marker beside it
     * explains. The band-cell form prints the label and asks a narrower question ({@code techHit})
     * instead.
     */
    protected valueHit(field: CmEdFieldView): boolean {
        return (field?.valueSegments ?? []).some(segment => segment.hit);
    }

    /**
     * Rebuilds the running-text lines whenever the fields change.
     *
     * Guarded on the input rather than run unconditionally: a size change alone switches which
     * template renders, and re-splitting the same array for it would be work with no result.
     */
    ngOnChanges(changes: SimpleChanges): void {

        if (changes["fields"]) {
            this.buildRunningLines();
        }
    }

    /**
     * Splits the printable fields into two lines of roughly equal length.
     *
     * Fields without a display value are dropped HERE and not on screen: in running text an empty
     * value would show up as a stray "· ·" that reads like a missing figure — on a screen whose
     * whole purpose is to prove nothing went missing.
     */
    protected buildRunningLines(): void {

        const printable = (this.fields ?? []).filter(field => !!field.displayValue);

        if (!printable.length) {
            this.runningLines = [];
            return;
        }

        const firstLineLength = Math.ceil(printable.length / 2);

        this.runningLines = [
            printable.slice(0, firstLineLength),
            printable.slice(firstLineLength),
        ].filter(line => line.length > 0);
    }
}
